import { useState, useEffect } from "react";
import { Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_LEAD_SOURCES = [
  "Facebook", "Facebook Ads", "Google", "Google Ads", "WhatsApp",
  "Website", "Instagram", "LinkedIn", "Referral", "Walk-in", "Manual", "Google Sheet"
];

const DEFAULT_STAGES = [
  "New Lead", "Contacted", "Interested", "Follow-up", "Negotiation",
  "Proposal Sent", "Won", "Lost", "Not Interested", "Do Not Disturb"
];

/**
 * Dynamic lead form fields driven by the Form Builder API.
 * Renders fields based on /api/lead-fields config for the current business.
 */
export function LeadFormFields({
  data, onChange, mode = "create",
  customFieldValues = {}, onCustomFieldChange,
  stages = DEFAULT_STAGES, sources = DEFAULT_LEAD_SOURCES,
  businessUsers = [], assignedTo, onAssignedToChange,
  hideMeeting = false,
}) {
  const [fields, setFields] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/lead-fields`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setFields(data.filter(f => f.is_active !== false));
        }
      } catch (e) { console.error("Failed to load form fields:", e); }
      finally { setLoaded(true); }
    })();
  }, []);

  const set = (key, val) => onChange({ ...data, [key]: val });

  // Determine which fields to show based on mode
  const visibleFields = fields.filter(f => {
    // Always show assigned_to in both create and edit
    if (f.field_key === "assigned_to") return true;
    if (mode === "create" && f.show_in_create === false) return false;
    if (mode === "edit" && f.show_in_edit === false) return false;
    // Skip meta fields that shouldn't be in the form
    if (["created_at", "updated_at"].includes(f.field_key)) return false;
    return true;
  });

  // Render a single field based on its config
  const renderField = (field) => {
    const key = field.field_key;
    const label = field.field_label;
    const type = field.field_type || "text";
    const required = field.is_required;
    const placeholder = field.placeholder || "";
    const options = field.options || [];
    const isSystem = field.is_system;

    // System field: assigned_to - use special dropdown
    if (key === "assigned_to") {
      if (!businessUsers.length || !onAssignedToChange) return null;
      return (
        <div key={key}>
          <Label>{label}{required && " *"}</Label>
          <Select value={assignedTo || ""} onValueChange={onAssignedToChange}>
            <SelectTrigger className="bg-slate-50 border border-slate-200 rounded-xl">
              <SelectValue placeholder="Select user" />
            </SelectTrigger>
            <SelectContent>
              {businessUsers.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // System field: source - use sources list
    if (key === "source") {
      return (
        <div key={key}>
          <Label>{label}{required && " *"}</Label>
          <Select value={data.source || ""} onValueChange={v => set("source", v)}>
            <SelectTrigger className="bg-slate-50 border border-slate-200 rounded-xl">
              <SelectValue placeholder={placeholder || "Select source"} />
            </SelectTrigger>
            <SelectContent>
              {sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // System field: stage - use stages list
    if (key === "stage") {
      return (
        <div key={key}>
          <Label>{label}{required && " *"}</Label>
          <Select value={data.stage || "New Lead"} onValueChange={v => set("stage", v)}>
            <SelectTrigger className="bg-slate-50 border border-slate-200 rounded-xl">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // System field: remark - textarea
    if (key === "remark") {
      return (
        <div key={key} className="col-span-full">
          <Label>{label}</Label>
          <Textarea placeholder={placeholder || "Add remarks..."} value={data.remark || ""}
            onChange={e => set("remark", e.target.value)}
            className="bg-slate-50 border border-slate-200 rounded-xl" rows={2} />
        </div>
      );
    }

    // For system fields, read/write from lead data directly
    // For custom fields, read/write from customFieldValues
    const isCustom = !isSystem;
    const fieldValue = isCustom
      ? (customFieldValues[key] || customFieldValues[field.field_id] || "")
      : (data[key] || "");
    const onFieldChange = isCustom
      ? (val) => onCustomFieldChange && onCustomFieldChange(key, val)
      : (val) => set(key, val);

    // Dropdown / Select
    if (type === "dropdown" || type === "select") {
      return (
        <div key={key}>
          <Label>{label}{required && " *"}</Label>
          <Select value={fieldValue} onValueChange={onFieldChange}>
            <SelectTrigger className="bg-slate-50 border border-slate-200 rounded-xl">
              <SelectValue placeholder={placeholder || `Select ${label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {(options || []).map(o => {
                const val = typeof o === "string" ? o : o.value;
                const lbl = typeof o === "string" ? o : o.label;
                return <SelectItem key={val} value={val}>{lbl}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // Textarea
    if (type === "textarea") {
      return (
        <div key={key} className="col-span-full">
          <Label>{label}{required && " *"}</Label>
          <Textarea placeholder={placeholder} value={fieldValue}
            onChange={e => onFieldChange(e.target.value)}
            className="bg-slate-50 border border-slate-200 rounded-xl" rows={2} />
        </div>
      );
    }

    // Date
    if (type === "date") {
      return (
        <div key={key}>
          <Label>{label}{required && " *"}</Label>
          <Input type="date" value={fieldValue} onChange={e => onFieldChange(e.target.value)}
            className="bg-slate-50 border border-slate-200 rounded-xl" />
        </div>
      );
    }

    // Number
    if (type === "number") {
      return (
        <div key={key}>
          <Label>{label}{required && " *"}</Label>
          <Input type="number" placeholder={placeholder} value={fieldValue}
            onChange={e => onFieldChange(e.target.value)}
            className="bg-slate-50 border border-slate-200 rounded-xl" />
        </div>
      );
    }

    // Default: text/email/phone input
    const inputType = type === "email" ? "email" : type === "phone" ? "tel" : "text";
    return (
      <div key={key}>
        <Label>{label}{required && " *"}</Label>
        <Input type={inputType} placeholder={placeholder} value={fieldValue}
          onChange={e => onFieldChange(e.target.value)}
          className="bg-slate-50 border border-slate-200 rounded-xl" />
      </div>
    );
  };

  // Fallback: if no fields loaded from API, show hardcoded basics
  if (loaded && visibleFields.length === 0) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Name *</Label>
            <Input value={data.name || ""} onChange={e => set("name", e.target.value)} className="bg-slate-50 border border-slate-200 rounded-xl" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={data.phone || ""} onChange={e => set("phone", e.target.value)} className="bg-slate-50 border border-slate-200 rounded-xl" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Email</Label>
            <Input type="email" value={data.email || ""} onChange={e => set("email", e.target.value)} className="bg-slate-50 border border-slate-200 rounded-xl" />
          </div>
          <div>
            <Label>Source</Label>
            <Select value={data.source || ""} onValueChange={v => set("source", v)}>
              <SelectTrigger className="bg-slate-50 border border-slate-200 rounded-xl"><SelectValue placeholder="Select source" /></SelectTrigger>
              <SelectContent>{sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Dynamic fields from Form Builder */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {visibleFields.map(renderField)}
      </div>

      {/* Meeting / Follow-up section */}
      {!hideMeeting && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
          <Label className="text-amber-700 font-semibold flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4" /> Next Meeting / Follow-up
          </Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-amber-600">Date & Time</Label>
              <Input type="datetime-local" value={data.next_meeting || ""} onChange={e => set("next_meeting", e.target.value)}
                className="bg-white border border-amber-200 rounded-xl text-sm" />
            </div>
            <div>
              <Label className="text-xs text-amber-600">Meeting Notes</Label>
              <Input placeholder="Add notes..." value={data.meeting_notes || ""} onChange={e => set("meeting_notes", e.target.value)}
                className="bg-white border border-amber-200 rounded-xl text-sm" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
